import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { API_URL } from '../../environment';
import { TimePicker, Checkbox, Tag, message } from 'antd';
import dayjs from 'dayjs';

interface Cadencia {
  cadencia: string;
  unidade: 'minutos' | 'horas' | 'dias'; // novo campo
  mensagem: string;
  // 🆕 Modo Template: cada cadência tem sua PRÓPRIA lista de templates + % (independente das outras).
  //    tpls[i] ↔ probs[i]. Persistidos em msg_i (nomes por vírgula) e prob_template_cad_i ("60,40").
  tpls?: string[];
  probs?: number[];
}


interface CadenciaResponse {
  message: string;
  cadencias: Cadencia[];
  ativar: string;
}

interface CadenciaModalProps {
  currentStage: string;
  currentFunnel: string;
  allCadencias: any[];
  show: boolean;
  onHide: () => void;
  verificarCloser: boolean;
  allStages: any[];
  idClienteOverride?: string | null; // 🆕 Prop opcional para Admin
  ehOficial?: boolean | null; // 🆕 Se o time usa número API Oficial (Meta) — habilita o switch Template
  onSaved?: () => void; // Callback chamado após salvar com sucesso para o pai refazer fetch
}


const CadenciaModal: React.FC<CadenciaModalProps> = ({
    currentStage,
    currentFunnel,
    allCadencias,
    show,
    onHide,
    verificarCloser,
    allStages,
    idClienteOverride = null, // 🆕 Default null
    ehOficial = null,
    onSaved,
  }) => {
  // Resolvendo o ID do cliente (prioridade para a prop)
  const resolvedIdCliente = idClienteOverride || localStorage.getItem("id_cliente");

  // 🆕 Time usa número API Oficial (Meta)? Prop tem prioridade; senão deriva do localStorage.
  // O switch Template/Serviço só aparece para números oficiais (templates Meta só funcionam neles).
  const ehOficialResolvido = ehOficial != null
    ? ehOficial
    : (() => {
        const v = localStorage.getItem("instanciaoficial");
        return !!(v && v !== "0" && v !== "null" && v !== "undefined");
      })();

  const [singleMessage, setSingleMessage] = useState("");
  const [singleMessageChecked, setSingleMessageChecked] = useState(false);
  const [cadenciaList, setCadenciaList] = useState<Cadencia[]>(
    Array(8).fill(null).map(() => ({ cadencia: "", unidade: "dias", mensagem: "" }))
  );
  const [useDateForFirst, setUseDateForFirst] = useState(false);

  const [dataLoaded, setDataLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const [midiaBoasVindasUrl, setMidiaBoasVindasUrl] = useState("");
  const [midiaBoasVindasTipo, setMidiaBoasVindasTipo] = useState("video");
  // funil.respeita_data_on da etapa — o estado guarda o valor CRU do banco, igual ao que o
  // motor lê. Semântica (bemvindo-worker/app/selecao.py, GATE_FUNIL):
  //   TRUE  = RESPEITA a janela (só envia no dia/hora configurados)
  //   FALSE = envia em QUALQUER horário (`respeita_data_on IS NOT TRUE` → elegível sempre)
  // A tela fala em "Qualquer horário", que é o INVERSO disto: por isso o botão exibe
  // `!respeitaDataOn`. Até 26/08/2026 o rótulo mostrava o valor direto e mentia — quem
  // marcava "Qualquer horário: SIM" na verdade LIGAVA a janela e travava o bem-vindo.
  const [respeitaDataOn, setRespeitaDataOn] = useState(true);
  const toggleRespeitaData = async () => {
    if (!currentFunnel || !currentStage) return;
    const novo = !respeitaDataOn;
    setRespeitaDataOn(novo); // otimista
    try {
      const res = await fetch(`${API_URL}/set_respeita_data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          funil: currentFunnel,
          etapa: currentStage,
          // `resolvedIdCliente`, nunca o localStorage cru: no Admin o modal abre a etapa de
          // OUTRO time (prop idClienteOverride) e o localStorage segue com o time do LOGIN.
          // Mandando o do login, o /set_respeita_data procurava o funil pai no time errado
          // e devolvia 404 "funil não encontrado" em toda etapa fora do time do operador.
          id_cliente: resolvedIdCliente,
          valor: novo,
        }),
      });
      // fetch NÃO rejeita em 404/500 — sem checar res.ok, o botão ficava no valor novo
      // dando a impressão de ter salvo. Também confere o rowcount: se a etapa não casar
      // por nome, a API responde ok com atualizados=0 e nada foi gravado.
      const body = await res.json().catch(() => ({} as any));
      if (!res.ok || body?.atualizados === 0) {
        throw new Error(body?.error || `falha ao salvar (HTTP ${res.status})`);
      }
    } catch (e: any) {
      setRespeitaDataOn(!novo); // reverte
      message.error(`Não foi possível alterar "Qualquer horário": ${e?.message || e}`);
      console.error("Erro ao alterar respeita_data_on:", e);
    }
  };
  const [midiaRetornoUrl, setMidiaRetornoUrl] = useState("");
  const [midiaRetornoTipo, setMidiaRetornoTipo] = useState("video");

  const [activeTab, setActiveTab] = useState<"cadencias" | "prompts" | "redirecionamento" | "cards" | "cards_back">("cadencias");
  // 🆕 As abas extras (Prompts, Redirecionamento, Cards) só aparecem quando o link
  //    da página traz ?prompt=1 na query string. Sem isso, o modal fica só com Cadências.
  const mostrarAbasPrompt = React.useMemo(
    () => new URLSearchParams(window.location.search).get("prompt") === "1",
    []
  );

  // 🆕 Switch Template vs Serviço + seletor de templates
  type ChatTemplate = { name: string; language: string; body: string; headerText: string; headerFormat: string; headerImage: string; vars: string[] };
  const [usaTemplate, setUsaTemplate] = useState(false);
  const [showTemplateInfo, setShowTemplateInfo] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templatesList, setTemplatesList] = useState<ChatTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateTarget, setTemplateTarget] = useState<((name: string) => void) | null>(null);
  // Quando o modo Template está ativo (em número oficial), os campos de texto ficam
  // bloqueados para digitação — só podem ser preenchidos via picker de template.
  const templateBloqueado = ehOficialResolvido && usaTemplate;

  // 🆕 ───── Múltiplos templates + probabilidade (só no modo Template) ─────
  // Boas-vindas: cada template escolhido tem seu PRÓPRIO %. Ao salvar,
  //   envio_mensagens.mensagem = nomes por vírgula e prob_template_bv = %s na mesma ordem.
  const [bvTemplates, setBvTemplates] = useState<{ name: string; prob: number }[]>([]);
  // Cadências: cada cadência tem sua PRÓPRIA lista de templates + % (independente das demais).
  //   Guardada em cada item de cadenciaList: item.tpls[] (nomes) alinhado a item.probs[] (%).
  //   Ex.: cad1 msg_1="t_a,t_b" prob_template_cad_1="60,40"; cad2 msg_2="t_c" prob_template_cad_2="100".
  // Mantém o picker aberto para adicionar vários templates seguidos (boas-vindas e cadências).
  const [pickerKeepOpen, setPickerKeepOpen] = useState(false);

  // Distribui 100 igualmente entre n posições (inteiros; o resto vai para os primeiros).
  const distribuirIgual = (n: number): number[] => {
    if (n <= 0) return [];
    const base = Math.floor(100 / n);
    const resto = 100 - base * n;
    return Array.from({ length: n }, (_, i) => base + (i < resto ? 1 : 0));
  };

  // Normaliza para somar 100 (inteiros) absorvendo a diferença no MAIOR dos OUTROS itens.
  // Regra pedida: ao mexer num valor, o maior dos demais se ajusta automaticamente p/ fechar 100.
  const normalizar100 = (arr: number[], changedIdx: number): number[] => {
    const vals = arr.map((v) => Math.max(0, Math.min(100, Math.round(v || 0))));
    if (vals.length === 0) return vals;
    if (vals.length === 1) return [100];
    let diff = 100 - vals.reduce((a, b) => a + b, 0);
    const others = vals.map((_, i) => i).filter((i) => i !== changedIdx);
    while (diff !== 0 && others.length) {
      others.sort((a, b) => vals[b] - vals[a]); // maior primeiro
      const t = others[0];
      if (diff > 0) { vals[t] += diff; diff = 0; }
      else {
        const rem = Math.min(vals[t], -diff);
        vals[t] -= rem; diff += rem;
        if (vals[t] === 0) others.shift();
      }
    }
    // Se ainda sobrou diferença (todos os outros zeraram), ajusta o próprio item mexido.
    if (diff !== 0) vals[changedIdx] = Math.max(0, vals[changedIdx] + diff);
    return vals;
  };

  const somaBv = bvTemplates.reduce((a, t) => a + (t.prob || 0), 0);
  // Soma das % de UMA cadência (cada uma é independente).
  const somaCadFor = (probs: number[] = []) => probs.reduce((a, b) => a + (b || 0), 0);

  // ── Boas-vindas: manipuladores ──
  const addBvTemplate = (name: string) => {
    setBvTemplates((prev) => {
      if (prev.some((t) => t.name === name)) return prev; // sem duplicados
      const nomes = [...prev.map((t) => t.name), name];
      const probs = distribuirIgual(nomes.length);
      return nomes.map((n, i) => ({ name: n, prob: probs[i] }));
    });
  };
  const removeBvTemplate = (idx: number) => {
    setBvTemplates((prev) => {
      const nomes = prev.filter((_, i) => i !== idx).map((t) => t.name);
      const probs = distribuirIgual(nomes.length);
      return nomes.map((n, i) => ({ name: n, prob: probs[i] }));
    });
  };
  const setBvProb = (idx: number, value: number) => {
    setBvTemplates((prev) => {
      const arr = prev.map((t) => t.prob);
      arr[idx] = value;
      const norm = normalizar100(arr, idx);
      return prev.map((t, i) => ({ ...t, prob: norm[i] }));
    });
  };

  // ── Cadências: manipuladores POR cadência (cada cadência = lista própria de templates + %) ──
  // Adiciona um template no fim da lista da cadência e re-distribui as % igualmente.
  const addCadTpl = (cadIdx: number, name: string) => {
    setCadenciaList((prev) => {
      const novo = [...prev];
      const it: any = { ...novo[cadIdx] };
      const tpls = [...((it.tpls as string[]) || [])];
      if (tpls.includes(name)) return prev; // sem duplicados na mesma cadência
      tpls.push(name);
      it.tpls = tpls;
      it.probs = distribuirIgual(tpls.length);
      novo[cadIdx] = it;
      return novo;
    });
  };
  // Remove o template da posição idx da cadência e re-distribui as % igualmente.
  const removeCadTpl = (cadIdx: number, idx: number) => {
    setCadenciaList((prev) => {
      const novo = [...prev];
      const it: any = { ...novo[cadIdx] };
      const tpls = ((it.tpls as string[]) || []).filter((_: string, i: number) => i !== idx);
      it.tpls = tpls;
      it.probs = distribuirIgual(tpls.length);
      novo[cadIdx] = it;
      return novo;
    });
  };
  // Ajusta a % de um template dentro da cadência (fecha 100 absorvendo no maior dos outros).
  const setCadTplProb = (cadIdx: number, idx: number, value: number) => {
    setCadenciaList((prev) => {
      const novo = [...prev];
      const it: any = { ...novo[cadIdx] };
      const arr = [...((it.probs as number[]) || [])];
      arr[idx] = value;
      it.probs = normalizar100(arr, idx);
      novo[cadIdx] = it;
      return novo;
    });
  };
  // Troca o template numa posição existente (mantém a % daquela posição).
  const changeCadTpl = (cadIdx: number, idx: number, name: string) => {
    setCadenciaList((prev) => {
      const novo = [...prev];
      const it: any = { ...novo[cadIdx] };
      const tpls = [...((it.tpls as string[]) || [])];
      while (tpls.length <= idx) tpls.push("");
      tpls[idx] = name;
      it.tpls = tpls;
      if (!it.probs || (it.probs as number[]).length !== tpls.length) it.probs = distribuirIgual(tpls.length);
      novo[cadIdx] = it;
      return novo;
    });
  };

  const [acaoEsperada, setAcaoEsperada] = useState("");
  const [objetivo, setObjetivo] = useState("");
  const [instrucoes, setInstrucoes] = useState("");
  // Prompt FINAL da etapa (funil.prompt_melhorado) — o único campo da aba Prompts.
  const [promptMelhorado, setPromptMelhorado] = useState("");
  const [instrucoesFunil, setInstrucoesFunil] = useState("");
  const [qtdeCaracteres, setQtdeCaracteres] = useState("Média");
  // Follow de Engajamento. O ESCOPO depende do funil (story 01.39): funil migrado
  // (`funil_pai.follow_por_etapa`) configura por ETAPA, em `funil`; os demais continuam
  // no funil pai, valendo para o funil inteiro. Coluna nula = follow desligado.
  // 🔴 NÃO HÁ HERANÇA no modo por etapa: etapa vazia é etapa muda, mesmo com o pai 'Sim'.
  // Por isso a leitura nunca cai no valor do pai como fallback quando o funil é migrado.
  const [followPorEtapa, setFollowPorEtapa] = useState(false);
  // Vem do /follow_fonte (proxy do webhook-meta). `followFonteMotivo` é o texto que o
  // BACKEND dá para o estado atual — usado no aviso em vez de a tela inventar o seu.
  const [followFonteMotivo, setFollowFonteMotivo] = useState("");
  const [followFreio, setFollowFreio] = useState(false);
  const [followPodeMigrar, setFollowPodeMigrar] = useState(false);
  const [followIdPai, setFollowIdPai] = useState<number | null>(null);
  const [migrandoFunil, setMigrandoFunil] = useState(false);
  // Snapshot do follow COMO FOI LIDO para a etapa aberta. O Salvar do modal grava tudo de
  // uma vez, inclusive quando o operador nunca abriu a aba Prompts — então o follow ia para
  // o banco sempre, carregando o que estivesse no state. Num teste isso LIGOU o follow de
  // uma etapa que estava 'Não', sem ninguém tocar no toggle. Comparando com o snapshot, o
  // request só sai quando algo mudou de fato: não mexeu, não manda.
  const followSnapshotRef = useRef<string | null>(null);
  const followAtualSerializado = () => JSON.stringify([
    followHabilitado, followIntervalos, followPrompt,
    followInstrucao1, followInstrucao2, followInstrucao3,
  ]);
  const [followPrompt, setFollowPrompt] = useState("");
  const [followHabilitado, setFollowHabilitado] = useState(false);
  const [followIntervalos, setFollowIntervalos] = useState("");
  // Papel de cada tentativa de follow. Vazio = usa só o prompt padrão do arquivo.
  // Se follow_intervalos tiver mais de 3 valores, as tentativas 4+ herdam a última preenchida.
  const [followInstrucao1, setFollowInstrucao1] = useState("");
  const [followInstrucao2, setFollowInstrucao2] = useState("");
  const [followInstrucao3, setFollowInstrucao3] = useState("");


  // Espelho do parser de `follow_intervalos` do webhook-meta. Existe porque um token ruim
  // no meio de tokens bons é descartado SOZINHO no backend, e o teto de follows encolhe
  // junto: o operador digita 6 intervalos, o cliente recebe 5, e nada avisa.
  const validarRegua = (csv: string): { tokens: number; erros: string[] } => {
    const bruto = (csv || "").trim();
    if (!bruto) return { tokens: 0, erros: [] };
    const erros: string[] = [];
    let tokens = 0;
    bruto.split(",").forEach((item) => {
      const tk = item.trim();
      if (!tk) { erros.push("item vazio"); return; }
      const horario = tk.match(/^(\d{2})[:h](\d{2})$/);   // HH:MM ou HHhMM, 2 dígitos cada
      if (horario) {
        const h = Number(horario[1]), m = Number(horario[2]);
        if (h > 23 || m > 59) { erros.push(`"${tk}": horário inexistente`); return; }
        tokens++; return;
      }
      if (/^\d+$/.test(tk)) {
        // Zero à esquerda é recusado pelo backend: "09" não vira 9h nem 9min.
        if (tk.length > 1 && tk.startsWith("0")) {
          erros.push(`"${tk}": zero à esquerda. Para 9h da manhã escreva 09:00`); return;
        }
        const n = Number(tk);
        if (n <= 0) { erros.push(`"${tk}": tem de ser maior que zero`); return; }
        if (n > 720) { erros.push(`"${tk}": acima do teto de 720 minutos (12h)`); return; }
        tokens++; return;
      }
      erros.push(`"${tk}": não é minutos nem HH:MM`);
    });
    return { tokens, erros };
  };
  const reguaInfo = validarRegua(followIntervalos);
  // Só bloqueia o salvamento no modo por etapa: no modo antigo (funil pai) a tela nunca
  // validou, e passar a barrar aqui impediria de salvar config que já está no banco.
  const reguaBloqueia = followPorEtapa && followHabilitado && reguaInfo.erros.length > 0;

  const [closerHabilitado, setCloserHabilitado] = useState(false);
  // Atender exige closer 'Sim' na etapa E no pai (critério do motor de follow).
  const [closerPai, setCloserPai] = useState<string | null>(null);
  const atendeDeFato = closerHabilitado && closerPai === "Sim";
  const [showDropdown, setShowDropdown] = useState(false);
  const [urlVideoFunil, setUrlVideoFunil] = useState("");
  const [urlImagemFunil, setUrlImagemFunil] = useState("");

  // --- Estados para Cards/Travas ---
  const [cardGroups, setCardGroups] = useState<any[]>([]);
  const [lockedFields, setLockedFields] = useState<number[]>([]);
  const [lockedFieldsBack, setLockedFieldsBack] = useState<number[]>([]); // New state for backward locks
  const [currentFunnelId, setCurrentFunnelId] = useState<number | null>(null);
  const [currentStageId, setCurrentStageId] = useState<number | null>(null);
  /* Etapa-meta 🏆 (Story 10 mobile): flag `funil.meta` no BD. Lê/grava pelo
     serviço cmob-push (/push/meta-etapa) — NÃO passa pelos endpoints deste
     modal de propósito (salvamento imediato e isolado do botão Salvar). */
  const [etapaMeta, setEtapaMeta] = useState(false);
  const [etapaMetaBusy, setEtapaMetaBusy] = useState(false);

  const [diasSelecionados, setDiasSelecionados] = useState<string[]>([]);
  const [horaInicio, setHoraInicio] = useState<string>("");
  const [horaFim, setHoraFim] = useState<string>("");

  const diasDaSemanaOpcoes = [
    "segunda-feira", "terça-feira", "quarta-feira", 
    "quinta-feira", "sexta-feira", "sábado", "domingo"
  ];

  const horasOpcoes = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString(),
    label: i.toString().padStart(2, '0')
  }));

  const toggleDia = (dia: string) => {
    setDiasSelecionados(prev => 
      prev.includes(dia) ? prev.filter(d => d !== dia) : [...prev, dia]
    );
  };

  const [fullScreenField, setFullScreenField] = useState<{
    title: string;
    value: string;
    onSave: (val: string) => void;
  } | null>(null);

  const openFullScreen = (title: string, value: string, onSave: (val: string) => void) => {
    setFullScreenField({ title, value, onSave });
  };

  // 🆕 ───── Seletor de templates (modo Template) ─────
  // Carrega os templates APROVADOS da WABA oficial do time. Mesmo endpoint do /chat.
  // No Admin (idClienteOverride setado) NÃO mandamos id_user — senão o backend resolve
  // a instância do admin logado, e não a do time selecionado.
  const loadAllTemplates = async (): Promise<ChatTemplate[]> => {
    const id_time = resolvedIdCliente;
    const ehAdmin = !!idClienteOverride;
    const ins_nome = localStorage.getItem("instanciaoficial");
    const id_user = ehAdmin ? null : localStorage.getItem("id_user");
    // `id_cliente` (grupo) + `waba: 'todas'` faz o backend varrer TODAS as WABAs do grupo,
    // como já faz a página /templates. Sem isso ele resolvia uma WABA só — a do primeiro
    // número do login (`instanciaoficial`, ex.: '29, 30' → 29) — e os templates das outras
    // WABAs do mesmo grupo nunca apareciam aqui: no TNP os 6 templates das instâncias
    // 53/54 (`isir_*`, incluindo o de boas-vindas) ficavam invisíveis no picker.
    const id_grupo = localStorage.getItem("id_grupo");
    const res = await fetch(`${API_URL}/api/whatsapp/templates`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_time, ins_nome, id_user, id_cliente: id_grupo, waba: "todas" }),
    });
    if (!res.ok) throw new Error("Falha ao carregar templates");
    const data: any[] = await res.json();
    const parse = (t: any): ChatTemplate => {
      let body = "", headerText = "", headerFormat = "", headerImage = "";
      for (const comp of t.components || []) {
        if (comp.type === "BODY") body = comp.text || "";
        if (comp.type === "HEADER") {
          headerFormat = comp.format || "";
          if (comp.format === "TEXT") headerText = comp.text || "";
          else if (comp.example?.header_handle?.length) headerImage = comp.example.header_handle[0];
        }
      }
      if ((headerFormat === "IMAGE" || t.tipo_midia === "imagem") && t.url_midia) {
        headerFormat = "IMAGE";
        headerImage = t.url_midia;
      }
      const nums = Array.from(
        new Set(((body + " " + headerText).match(/\{\{(\d+)\}\}/g) || []).map((m: string) => m.replace(/\D/g, "")))
      ).sort((a, b) => Number(a) - Number(b));
      return { name: t.name, language: t.language, body, headerText, headerFormat, headerImage, vars: nums };
    };
    return data.filter((t) => (t.status || "").toUpperCase() === "APPROVED").map(parse);
  };

  // Abre o picker e injeta o callback que grava o NOME do template no campo alvo.
  // keepOpen=true mantém o picker aberto p/ adicionar vários (usado nas boas-vindas).
  const openTemplatePicker = async (apply: (name: string) => void, keepOpen = false) => {
    setPickerKeepOpen(keepOpen);
    setTemplateTarget(() => apply);
    setTemplateSearch("");
    setShowTemplatePicker(true);
    setLoadingTemplates(true);
    try {
      setTemplatesList(await loadAllTemplates());
    } catch (err) {
      console.error(err);
      message.error("Erro ao carregar os templates da conta.");
      setShowTemplatePicker(false);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Ao escolher: grava o nome do template no campo. Em keepOpen, mantém o picker
  // aberto para adicionar mais templates (boas-vindas com vários templates).
  const chooseTemplate = (t: ChatTemplate) => {
    templateTarget?.(t.name);
    if (pickerKeepOpen) {
      message.success(`Template "${t.name}" adicionado.`);
    } else {
      setShowTemplatePicker(false);
      setTemplateTarget(null);
    }
  };

  const fieldTypes: { [key: string]: string } = {
    text: "Texto",
    number: "Número",
    date: "Data",
    currency: "Moeda",
    select: "Seleção",
    file: "Arquivo"
  };


  // 🧭 Redirecionamentos: 3 blocos com { instrucao, etapa_destino_id }
  const [redirecionamentos, setRedirecionamentos] = useState<
    Array<{ instrucao: string; etapa_destino_id: string }>
  >([
    { instrucao: "", etapa_destino_id: "" },
    { instrucao: "", etapa_destino_id: "" },
    { instrucao: "", etapa_destino_id: "" },
  ]);

  const handleInstrucaoChange = (index: number, value: string) => {
    setRedirecionamentos(prev => {
      const novo = [...prev];
      novo[index].instrucao = value;
      return novo;
    });
  };

  const handleEtapaChange = (index: number, value: string) => {
    setRedirecionamentos(prev => {
      const novo = [...prev];
      novo[index].etapa_destino_id = value;
      return novo;
    });
  };


  const handleUploadType = (tipo: string) => {
    setMidiaBoasVindasTipo(tipo);
    setShowDropdown(false);

    // abre seletor de arquivo conforme tipo
    const input = document.createElement("input");
    input.type = "file";
    input.accept =
      tipo === "image"
        ? "image/*"
        : tipo === "video"
        ? "video/*"
        : ".pdf,.doc,.docx,.xlsx";
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("mediatype", tipo);

      try {
        const res = await fetch(`${API_URL}/upload_cadencia_midia`, {
          method: "POST",
          body: formData,
        });
        const data = await res.json();
        if (data.url) {
          setMidiaBoasVindasUrl(data.url);
        } else {
          message.error("Erro ao enviar mídia");
        }
      } catch (err) {
        message.error("Falha no envio");
      }
    };
    input.click();
  };

  const fetchCardStructure = async (funnelId: number, stageId: number) => {
    try {
      // 1. Grupos (do funil e padrão)
      const resGroups = await fetch(`${API_URL}/card/groups?id_funil_pai=${funnelId}&id_cliente=${resolvedIdCliente}`);
      if (!resGroups.ok) return;
      const groups = await resGroups.json();

      // 2. Campos de cada grupo
      const groupsWithFields = await Promise.all(groups.map(async (g: any) => {
        const resFields = await fetch(`${API_URL}/card/fields?id_grupo=${g.id}`);
        const fields = resFields.ok ? await resFields.json() : [];
        return { ...g, fields };
      }));
      setCardGroups(groupsWithFields);

      // 3. Travas atuais (Avanço)
      const resLocks = await fetch(`${API_URL}/card/locks?id_etapa=${stageId}&tipo=avanco`);
      if (resLocks.ok) {
        const locks = await resLocks.json();
        setLockedFields(locks);
      }

      // 4. Travas atuais (Retrocesso)
      const resLocksBack = await fetch(`${API_URL}/card/locks?id_etapa=${stageId}&tipo=retrocesso`);
      if (resLocksBack.ok) {
        const locksBack = await resLocksBack.json();
        setLockedFieldsBack(locksBack);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const toggleLock = (fieldId: number) => {
    setLockedFields(prev => {
      if (prev.includes(fieldId)) return prev.filter(id => id !== fieldId);
      return [...prev, fieldId];
    });
  };

  const toggleLockBack = (fieldId: number) => {
    setLockedFieldsBack(prev => {
      if (prev.includes(fieldId)) return prev.filter(id => id !== fieldId);
      return [...prev, fieldId];
    });
  };


  useEffect(() => {
    if (show && currentStage && currentFunnel) {
      // Sem ?prompt=1 as abas extras somem — força a visão na aba Cadências
      // (as seções de Prompts/Redirecionamento/Cards renderizam por activeTab).
      if (!mostrarAbasPrompt) setActiveTab("cadencias");

      const cadRecord = allCadencias.find(
        (r) => r.funil === currentFunnel && r.etapa === currentStage
      );

      const etapaRecord = allStages.find(
        (r) => r.funil === currentFunnel && r.nome_funil === currentStage
      );

      if (cadRecord || etapaRecord) {
        setSingleMessage(cadRecord?.message || "");
        setSingleMessageChecked(cadRecord?.ativar === "Sim");
        setReturnMessage(cadRecord?.mensagem_retorno || "");

        // 🆕 Modo Template ativo quando funil.template === 'Sim' no banco
        const tmplMode = cadRecord?.template === "Sim";
        setUsaTemplate(tmplMode);

        // 🆕 Espelho de probabilidades (só faz sentido no modo Template).
        //   Boas-vindas: mensagem = "t1,t2,t3"       ·  prob_template_bv    = "20,20,60"
        //   Cadências:   cada msg_i = "t_a,t_b"       ·  prob_template_cad_i = "60,40" (POR cadência)
        //   (as % das cadências são parseadas por item mais abaixo, em cadList)
        if (tmplMode) {
          const nomesBv = (cadRecord?.message || "")
            .split(",").map((s: string) => s.trim()).filter(Boolean);
          const probsBv = (cadRecord?.prob_template_bv || "")
            .split(",").map((s: string) => parseInt(s.trim(), 10));
          const bv = nomesBv.map((n: string, i: number) => ({
            name: n,
            prob: !isNaN(probsBv[i]) ? probsBv[i] : 0,
          }));
          // Se as % não fecham 100 (dado legado/inconsistente), distribui igualmente.
          if (bv.length && bv.reduce((a: number, t: any) => a + t.prob, 0) !== 100) {
            const d = distribuirIgual(bv.length);
            bv.forEach((t: any, i: number) => { t.prob = d[i]; });
          }
          setBvTemplates(bv);
        } else {
          setBvTemplates([]);
        }

        setMidiaBoasVindasUrl(cadRecord?.bem_vindo_url || "");
        setMidiaRetornoUrl(cadRecord?.data_cad_url || "");
        // respeita_data_on: default TRUE (coluna nasce true); só fica false se vier false explícito
        setRespeitaDataOn(cadRecord?.respeita_data_on !== false);

        setAcaoEsperada(etapaRecord?.acao_esperada || "");

        if (cadRecord) {
          // Converte "segunda-feira, terça-feira" em ["segunda-feira", "terça-feira"]
          const dias = cadRecord.dias_semana ? cadRecord.dias_semana.split(', ') : [];
          setDiasSelecionados(dias);
          
          // Garante que o hor�rio venha formatado com 2 dígitos (ex: "09")
          setHoraInicio(cadRecord.hora_inicio || "");
          setHoraFim(cadRecord.hora_fim || "");
        }

        if (etapaRecord) {
          setCurrentStageId(etapaRecord.id); // Salva ID da etapa
          // Carrega o estado da etapa-meta 🏆 (best effort — serviço fora = switch off)
          setEtapaMeta(false);
          fetch(`/push/meta-etapa?id=${etapaRecord.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("supabase_token") || ""}` },
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (d) setEtapaMeta(!!d.meta); })
            .catch(() => {});
          setRedirecionamentos([
            {
              instrucao: etapaRecord.instrucoes_avanco_1 || "",
              etapa_destino_id: etapaRecord.etapa_avanco_1 || "",
            },
            {
              instrucao: etapaRecord.instrucoes_avanco_2 || "",
              etapa_destino_id: etapaRecord.etapa_avanco_2 || "",
            },
            {
              instrucao: etapaRecord.instrucoes_avanco_3 || "",
              etapa_destino_id: etapaRecord.etapa_avanco_3 || "",
            },
          ]);

          // Tenta buscar o ID do Funil Pai para carregar Cards
          fetch(`${API_URL}/card/funnels?id_cliente=${resolvedIdCliente}`)
            .then(r => r.json())
            .then(funnels => {
              const f = funnels.find((f: any) => f.nome === currentFunnel);
              if (f) {
                setCurrentFunnelId(f.id);
                fetchCardStructure(f.id, etapaRecord.id);
              }
            });
        }

        setObjetivo(etapaRecord?.objetivo || "");
        setInstrucoes(etapaRecord?.instrucoes || "");
        setPromptMelhorado(etapaRecord?.prompt_melhorado || "");
        setInstrucoesFunil(etapaRecord?.instrucoes_funil || "");
        setQtdeCaracteres(etapaRecord?.qtde_caracteres || "Média");
        // Follow: a origem depende do funil. Migrado (`follow_por_etapa`) lê os campos da
        // ETAPA; os demais seguem lendo do funil pai, como antes.
        // 🔴 No modo por etapa NÃO existe fallback para o pai — nem no valor, nem no
        // placeholder. Campo vazio significa "esta etapa não faz follow", e mostrar o valor
        // do pai faria o operador achar que está valendo.
        // NULL/ausente é lido como "Não": o gate do worker só aceita literalmente 'Sim'.
        // A coluna `follow_por_etapa` é o que o CRM PEDIU; o /follow_fonte é o que o
        // backend FEZ com o pedido. Divergem quando a guarda recusa a flag (pai marcado
        // sem nenhuma etapa configurada) ou quando o freio de emergência está ligado.
        // Nesses casos vale o backend, senão a tela promete edição que não tem efeito.
        // Começa pela coluna para a tela não piscar, e corrige quando a resposta chega.
        const migrado = etapaRecord?.follow_por_etapa === true;
        setFollowPorEtapa(migrado);
        setFollowFonteMotivo("");
        setFollowFreio(false);
        setFollowPodeMigrar(false);
        setFollowIdPai(etapaRecord?.id_pai ?? null);
        if (etapaRecord?.id_pai) {
          fetch(`${API_URL}/follow_fonte?id_pai=${etapaRecord.id_pai}`)
            .then((r) => r.json())
            .then((d) => {
              // Endpoint fora do ar: mantém o que a coluna disse (modo conservador).
              if (d?.indisponivel) return;
              const f = (d?.fontes || [])[0];
              if (!f) return;
              setFollowPorEtapa(f.fonte === "etapa");
              setFollowFonteMotivo(f.motivo || "");
              setFollowFreio(d?.freio === true);
              setFollowPodeMigrar(f.pode_migrar === true);
            })
            .catch(() => { /* silencioso: a coluna já deu um default seguro */ });
        }
        if (migrado) {
          setFollowHabilitado(etapaRecord?.follow_etapa === "Sim");
          setFollowIntervalos(etapaRecord?.follow_intervalos_etapa || "");
          setFollowPrompt(etapaRecord?.follow_prompt_etapa || "");
          setFollowInstrucao1(etapaRecord?.follow_instrucao_1_etapa || "");
          setFollowInstrucao2(etapaRecord?.follow_instrucao_2_etapa || "");
          setFollowInstrucao3(etapaRecord?.follow_instrucao_3_etapa || "");
        } else {
          setFollowHabilitado(etapaRecord?.follow_funil === "Sim");
          setFollowIntervalos(etapaRecord?.follow_intervalos_funil || "");
          setFollowPrompt(etapaRecord?.follow_prompt_funil || "");
          setFollowInstrucao1(etapaRecord?.follow_instrucao_1_funil || "");
          setFollowInstrucao2(etapaRecord?.follow_instrucao_2_funil || "");
          setFollowInstrucao3(etapaRecord?.follow_instrucao_3_funil || "");
        }
        // Snapshot do que veio do banco. `etapaRecord` ausente = leitura não confiável:
        // marca como null e o salvamento pula o follow em vez de gravar um chute.
        followSnapshotRef.current = etapaRecord
          ? JSON.stringify([
              (migrado ? etapaRecord?.follow_etapa : etapaRecord?.follow_funil) === "Sim",
              (migrado ? etapaRecord?.follow_intervalos_etapa : etapaRecord?.follow_intervalos_funil) || "",
              (migrado ? etapaRecord?.follow_prompt_etapa : etapaRecord?.follow_prompt_funil) || "",
              (migrado ? etapaRecord?.follow_instrucao_1_etapa : etapaRecord?.follow_instrucao_1_funil) || "",
              (migrado ? etapaRecord?.follow_instrucao_2_etapa : etapaRecord?.follow_instrucao_2_funil) || "",
              (migrado ? etapaRecord?.follow_instrucao_3_etapa : etapaRecord?.follow_instrucao_3_funil) || "",
            ])
          : null;
        setCloserHabilitado(etapaRecord?.closer === "Sim");
        setCloserPai(etapaRecord?.closer_pai ?? null);
        setUrlVideoFunil(etapaRecord?.url_vid || "");
        setUrlImagemFunil(etapaRecord?.url_img || "");


        let cadList = cadRecord?.cadencias?.map((c: any) => {
          const tempo = parseInt(c.cadencia) || 0;
          let unidade: 'minutos' | 'horas' | 'dias' = 'minutos';
          let valorTempo = tempo.toString();

          if (tempo % 1440 === 0 && tempo !== 0) {
            valorTempo = (tempo / 1440).toString();
            unidade = "dias";
          } else if (tempo % 60 === 0 && tempo !== 0) {
            valorTempo = (tempo / 60).toString();
            unidade = "horas";
          }

          // 🔹 No modo Template, cada cadência guarda os NOMES dos templates por vírgula
          //    (c.mensagem) e as SUAS % em c.prob ("60,40"), alinhados 1:1.
          const tpls = tmplMode
            ? (c.mensagem || "").split(",").map((s: string) => s.trim()).filter(Boolean)
            : [];
          let probs = tmplMode
            ? (c.prob || "").split(",").map((s: string) => parseInt(s.trim(), 10)).filter((n: number) => !isNaN(n))
            : [];
          // Alinha % ao nº de templates; se faltar/sobrar ou não fechar 100, distribui igualmente.
          if (tmplMode) {
            if (probs.length !== tpls.length || (tpls.length && probs.reduce((a: number, b: number) => a + b, 0) !== 100)) {
              probs = distribuirIgual(tpls.length);
            }
          }

          // 🔹 Inclui as propriedades de mídia enviadas pelo backend
          return {
            cadencia: valorTempo,
            unidade,
            mensagem: c.mensagem || "",
            midiaUrl: c.midiaUrl || "",
            midiaTipo: c.midiaTipo || "video",
            tpls,
            probs,
          };
        }) || [];

        while (cadList.length < 8)
          cadList.push({ cadencia: "", mensagem: "", unidade: "dias", tpls: [], probs: [] });
        setCadenciaList(cadList.slice(0, 8));
      } else {
        // reset
        setSingleMessage(""); setSingleMessageChecked(false); setReturnMessage("");
        setAcaoEsperada(""); setObjetivo(""); setInstrucoes(""); setInstrucoesFunil(""); setCloserHabilitado(false);
        setBvTemplates([]);
        setCadenciaList([
          { cadencia: "", mensagem: "", unidade: "dias" },
          { cadencia: "", mensagem: "", unidade: "dias" },
          { cadencia: "", mensagem: "", unidade: "dias" },
        ]);
        setRedirecionamentos([
          { instrucao: "", etapa_destino_id: "" },
          { instrucao: "", etapa_destino_id: "" },
          { instrucao: "", etapa_destino_id: "" },
        ]);
      }

      setDataLoaded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentStage, currentFunnel, resolvedIdCliente]);
  
  

  if (!dataLoaded) {
    return <div>Carregando cadências...</div>; // Ou um spinner
  }

  const handleAplicar = async () => {
    // Régua inválida barra o salvamento ANTES de qualquer request: o backend aceitaria
    // e descartaria os tokens ruins em silêncio, encolhendo o teto de follows sem avisar.
    if (reguaBloqueia) {
      alert("Corrija a régua do follow antes de salvar:\n\n" + reguaInfo.erros.join("\n"));
      return;
    }
    setIsSaving(true);
    try {
      // 1. Salvar Redirecionamentos (se houver)
      const redirecionamentosLimpos = redirecionamentos
        .filter(r => r.etapa_destino_id && r.etapa_destino_id !== "")
        .map(r => ({
          instrucao: r.instrucao?.trim() || "",
          etapa_destino_id: Number(r.etapa_destino_id)
        }));

      if (redirecionamentosLimpos.length > 0) {
        const resRedir = await fetch(`${API_URL}/salvar_redirecionamento`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_cliente: resolvedIdCliente,
            nome_funil: currentFunnel,
            nome_etapa: currentStage,
            redirecionamentos: redirecionamentosLimpos
          }),
        });
        if (!resRedir.ok) {
           const data = await resRedir.json();
           throw new Error(data?.error || "Erro ao salvar redirecionamentos");
        }
      }

      // 2. Salvar Cards (Travas) - Avanço e Retrocesso
      if (currentStageId) {
        // Avanço
        await fetch(`${API_URL}/card/locks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_etapa: currentStageId,
            id_campos: lockedFields,
            tipo: "avanco"
          }),
        });

        // Retrocesso
        await fetch(`${API_URL}/card/locks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_etapa: currentStageId,
            id_campos: lockedFieldsBack,
            tipo: "retrocesso"
          }),
        });
      }

      // 3. Preparar e Salvar Cadências
      const midias = cadenciaList
        .map((item, i) => {
          const url = (item as any).midiaUrl;
          const tipo = (item as any).midiaTipo;
          if (url && tipo) {
            const formatted = url.includes(" | ") ? url : `${url} | ${tipo}`;
            return { index: i + 1, url: formatted };
          }
          return null;
        })
        .filter(Boolean);

      // 🆕 No modo Template, a "mensagem" (boas-vindas) e cada cadência guardam os NOMES
      //    dos templates por vírgula; as % vão nas colunas espelho prob_template_bv/cad.
      const tmplOn = ehOficialResolvido && usaTemplate;
      const bvMessage = tmplOn
        ? (bvTemplates.map((t) => t.name).join(",") || null)
        : (singleMessage && singleMessage.trim() !== "" ? singleMessage.trim() : null);
      const probBv = tmplOn ? bvTemplates.map((t) => t.prob).join(",") : "";

      const payload = {
        etapa: currentStage ? (currentStage.trim() === "" ? null : currentStage.trim()) : null,
        funil: currentFunnel ? (currentFunnel.trim() === "" ? null : currentFunnel.trim()) : null,
        message: bvMessage,
        checkbox: singleMessageChecked,
        dias_semana: diasSelecionados.join(', '),
        hora_inicio: horaInicio,
        hora_fim: horaFim,
        cadencias: cadenciaList.map((item) => {
          const valor = parseInt(item.cadencia || "0", 10);
          let minutos = 0;

          if (item.unidade === "minutos") minutos = valor;
          else if (item.unidade === "horas") minutos = valor * 60;
          else if (item.unidade === "dias") minutos = valor * 1440;

          // 🆕 No modo Template: só os templates COM % > 0 vão pro banco (nomes e % alinhados).
          const tplsRaw = (((item as any).tpls as string[]) || []);
          const probsRaw = (((item as any).probs as number[]) || []);
          const nomes: string[] = [];
          const percs: number[] = [];
          tplsRaw.forEach((nome, i) => {
            const n = (nome || "").trim();
            if (n) { nomes.push(n); percs.push(probsRaw[i] || 0); }
          });
          const mensagem = tmplOn
            ? (nomes.join(",") || null)
            : (item.mensagem?.trim() || null);
          // % desta cadência (posicional, alinhada aos nomes). Vazio em modo Serviço.
          const prob = tmplOn ? percs.join(",") : "";

          return {
            cadencia: isNaN(minutos) || minutos === 0 ? null : minutos.toString(),
            mensagem,
            prob, // 🆕 grava em prob_template_cad_{i}
          };
        }),
        midias,
        midia_boasvindas:
          midiaBoasVindasUrl && midiaBoasVindasTipo
            ? `${midiaBoasVindasUrl} | ${midiaBoasVindasTipo}`
            : "",
        use_date_for_first: useDateForFirst,
        // 🆕 vai junto do Salvar: alterar só o botão "Qualquer horário" e salvar persiste,
        //    mesmo sem mexer na mensagem inicial (antes dependia só do clique no toggle).
        respeita_data_on: respeitaDataOn,
        usa_template: tmplOn, // 🆕 só envia template se for oficial
        prob_template_bv: probBv,   // 🆕 espelho de % das boas-vindas
        id_cliente: resolvedIdCliente,
      };

      const resCad = await fetch(`${API_URL}/marcarcadencias`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resCad.ok) throw new Error("Erro ao salvar cadências");

      // 4. Salvar Mensagem Retorno
      const retornoPayload = {
        etapa: payload.etapa,
        funil: payload.funil,
        message: returnMessage.trim() || null,
        midia_retorno:
          midiaRetornoUrl && midiaRetornoTipo
            ? `${midiaRetornoUrl} | ${midiaRetornoTipo}`
            : "",
        id_cliente: payload.id_cliente,
      };
      const resRet = await fetch(`${API_URL}/marcarmensagemretorno`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(retornoPayload),
      });
      if (!resRet.ok) throw new Error("Erro ao salvar mensagem de retorno");

      // 5. Salvar Prompts e Mídias do Funil
      // A aba Prompts agora edita o PROMPT FINAL. Salva por /salvar_prompt_melhorado, que
      // grava só `prompt_melhorado` (+ qtde_caracteres).
      // NÃO usa mais /salvarprompts_etapa: aquele endpoint é o fluxo do gerador — grava
      // ação/objetivo/instruções, zera o prompt_melhorado e marca fluxo_melhorar='Sim' para a
      // IA reescrever. Chamá-lo aqui apagaria o texto que o usuário acabou de escrever (e,
      // como a tela não tem mais os 3 campos, gravaria vazio por cima do que está no banco).
      const resPrompts = await fetch(`${API_URL}/salvar_prompt_melhorado`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome_funil: currentFunnel,
          nome_etapa: currentStage,
          id_cliente: resolvedIdCliente,
          prompt_melhorado: promptMelhorado,
          qtde_caracteres: qtdeCaracteres,
        }),
      });
      if (!resPrompts.ok) throw new Error("Erro ao salvar o prompt da etapa");

      // Follow de Engajamento — rota conforme o ESCOPO em vigor (story 01.39):
      //   funil migrado     -> /salvar_follow_etapa, grava na `funil` (só esta etapa)
      //   funil não migrado -> /salvar_follow_funil, grava na `funil_pai` (funil inteiro)
      // Editar sempre o que está valendo evita as duas falhas possíveis: gravar na etapa
      // num funil que o backend ainda lê do pai (mudança sem efeito), ou gravar no pai de
      // um funil migrado (campo morto e duas verdades para a mesma coisa).
      // 'Sim'/'Não' com maiúscula: o gate do worker exige literalmente 'Sim'.
      // Só grava o follow se ele MUDOU em relação ao que foi lido (ver followSnapshotRef).
      // Snapshot null = a leitura não achou a etapa: não dá para saber o valor certo, então
      // não grava nada — melhor deixar como está do que sobrescrever com um chute.
      const followMudou = followSnapshotRef.current !== null
        && followSnapshotRef.current !== followAtualSerializado();
      const resFollow = !followMudou
        ? new Response(JSON.stringify({ ok: true, ignorado: "follow inalterado" }), { status: 200 })
        : followPorEtapa
        ? await fetch(`${API_URL}/salvar_follow_etapa`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id_etapa: currentStageId,
              follow: followHabilitado ? "Sim" : "Não",
              follow_intervalos: followIntervalos,
              follow_prompt: followPrompt,
              follow_instrucao_1: followInstrucao1,
              follow_instrucao_2: followInstrucao2,
              follow_instrucao_3: followInstrucao3,
            }),
          })
        : await fetch(`${API_URL}/salvar_follow_funil`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              nome_funil: currentFunnel,
              id_cliente: resolvedIdCliente,
              follow: followHabilitado ? "Sim" : "Não",
              follow_intervalos: followIntervalos,
              follow_prompt: followPrompt,
              follow_instrucao_1: followInstrucao1,
              follow_instrucao_2: followInstrucao2,
              follow_instrucao_3: followInstrucao3,
            }),
          });
      if (!resFollow.ok) throw new Error("Erro ao salvar o Follow de Engajamento");

      const resMidiaFunil = await fetch(`${API_URL}/salvar_midias_funil`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: resolvedIdCliente,
          nome_funil: currentFunnel,
          nome_etapa: currentStage,
          url_vid: urlVideoFunil || null,
          url_img: urlImagemFunil || null,
        }),
      });
      if (!resMidiaFunil.ok) throw new Error("Erro ao salvar mídias do funil");

      // Notifica o pai para refazer fetch (cadências, etapas, funis) antes de fechar
      onSaved?.();

      message.success("Todas as configurações foram salvas com sucesso!");
      onHide();

    } catch (error) {
      console.error(error);
      const msg =
        error instanceof Error
          ? error.message
          : typeof error === "string"
          ? error
          : JSON.stringify(error);
      message.error(msg);
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <>
    <Modal show={show} onHide={onHide} centered dialogClassName="tp-cadencia-modal">
      <Modal.Header closeButton>
        <Modal.Title><i className="ti ti-settings me-2" />Configurar Etapa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* 🏆 Etapa de meta: grava funil.meta na hora (serviço cmob-push), fora do Salvar */}
        <div
          className="d-flex align-items-center justify-content-between gap-2 mb-3 p-2 rounded-3"
          style={{ background: "#fefce8", border: "1px solid #fde68a" }}
        >
          <div>
            <span className="fw-bold" style={{ fontSize: 13, color: "#713f12" }}>
              🏆 Etapa de meta
            </span>
            <div style={{ fontSize: 12, color: "#a16207" }}>
              Leads que chegarem aqui contam como meta atingida (troféu + push no app mobile). Salva na hora.
            </div>
          </div>
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={etapaMeta}
              disabled={etapaMetaBusy || !currentStageId}
              onChange={async () => {
                if (!currentStageId) return;
                const novo = !etapaMeta;
                setEtapaMetaBusy(true);
                setEtapaMeta(novo); // otimista
                try {
                  const r = await fetch("/push/meta-etapa", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${localStorage.getItem("supabase_token") || ""}`,
                    },
                    body: JSON.stringify({ id_etapa: currentStageId, meta: novo }),
                  });
                  if (!r.ok) throw new Error();
                  message.success(novo ? "Etapa marcada como meta 🏆" : "Etapa desmarcada como meta");
                } catch {
                  setEtapaMeta(!novo); // reverte
                  message.error("Não deu pra salvar a etapa-meta. Tente de novo.");
                } finally {
                  setEtapaMetaBusy(false);
                }
              }}
            />
          </div>
        </div>

        {/* 🆕 Switch Template vs Serviço — só para números API Oficial (templates Meta) */}
        {ehOficialResolvido && (
          <div
            className="d-flex align-items-center gap-2 mb-3 p-2 rounded-3"
            style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", flexWrap: "wrap" }}
          >
            <span className="fw-bold" style={{ fontSize: 13, color: "#334155" }}>Modo de envio:</span>
            <div
              className="d-inline-flex"
              style={{ background: "#e2e8f0", borderRadius: 999, padding: 3 }}
              role="group"
            >
              <button
                type="button"
                onClick={() => setUsaTemplate(false)}
                style={{
                  border: "none", borderRadius: 999, padding: "5px 16px", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", transition: "all .15s",
                  background: !usaTemplate ? "#ffffff" : "transparent",
                  color: !usaTemplate ? "#0f172a" : "#64748b",
                  boxShadow: !usaTemplate ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                }}
              >
                <i className="ti ti-message-circle me-1" />Serviço
              </button>
              <button
                type="button"
                onClick={() => {
                  setUsaTemplate(true);
                  // Semeia a partir do que já existe (texto vira nomes; % distribuídas igualmente).
                  setBvTemplates((prev) => {
                    if (prev.length) return prev;
                    const nomes = (singleMessage || "").split(",").map((s) => s.trim()).filter(Boolean);
                    if (!nomes.length) return [];
                    const d = distribuirIgual(nomes.length);
                    return nomes.map((n, i) => ({ name: n, prob: d[i] }));
                  });
                  setCadenciaList((prev) => prev.map((it) => {
                    if ((((it as any).tpls as string[]) || []).length) return it;
                    const nomes = (it.mensagem || "").split(",").map((s) => s.trim()).filter(Boolean);
                    const novo = { ...it } as Cadencia;
                    (novo as any).tpls = nomes;
                    (novo as any).probs = distribuirIgual(nomes.length);
                    return novo;
                  }));
                }}
                style={{
                  border: "none", borderRadius: 999, padding: "5px 16px", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", transition: "all .15s",
                  background: usaTemplate ? "#16a34a" : "transparent",
                  color: usaTemplate ? "#ffffff" : "#64748b",
                  boxShadow: usaTemplate ? "0 1px 3px rgba(22,163,74,0.35)" : "none",
                }}
              >
                <i className="ti ti-template me-1" />Template
              </button>
            </div>
            {/* Botão info com explicação da regra de 24h */}
            <div className="position-relative d-inline-block">
              <button
                type="button"
                onClick={() => setShowTemplateInfo((p) => !p)}
                onBlur={() => setTimeout(() => setShowTemplateInfo(false), 150)}
                title="Por que usar template?"
                style={{
                  border: "none", background: "transparent", color: "#64748b",
                  cursor: "pointer", display: "inline-flex", alignItems: "center", padding: 2,
                }}
              >
                <i className="ti ti-info-circle" style={{ fontSize: 18 }} />
              </button>
              {showTemplateInfo && (
                <div
                  style={{
                    position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 1060,
                    width: 280, background: "#ffffff", border: "1px solid #e2e8f0",
                    borderRadius: 10, padding: "10px 12px", boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
                    fontSize: 12, lineHeight: 1.5, color: "#334155",
                  }}
                >
                  <div className="fw-bold mb-1" style={{ color: "#16a34a" }}>
                    <i className="ti ti-clock-hour-4 me-1" />Janela de 24h da Meta
                  </div>
                  Para enviar a <b>mensagem de boas-vindas</b> ou qualquer mensagem
                  <b> após 24h</b> sem resposta do cliente, a Meta exige um <b>template aprovado</b>.
                  Ative o modo <b>Template</b> para escolher templates nos campos abaixo.
                </div>
              )}
            </div>
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
          <div className="tp-cadencia-info">
            <span className="tp-info-label">Funil:</span> {currentFunnel}
            <span className="tp-info-divider">|</span>
            <span className="tp-info-label">Etapa:</span> {currentStage}
          </div>
          {verificarCloser && mostrarAbasPrompt && (
            <div className="tp-tab-nav">
              <button className={`tp-tab-btn ${activeTab === "cadencias" ? "active" : ""}`} onClick={() => setActiveTab("cadencias")}>Cadências</button>
              <button className={`tp-tab-btn ${activeTab === "prompts" ? "active" : ""}`} onClick={() => setActiveTab("prompts")}>Prompts</button>
              <button className={`tp-tab-btn ${activeTab === "redirecionamento" ? "active" : ""}`} onClick={() => setActiveTab("redirecionamento")}>Redirecionamento</button>
              <button className={`tp-tab-btn ${activeTab === "cards" ? "active" : ""}`} onClick={() => setActiveTab("cards")}>Cards (Avanço)</button>
              <button className={`tp-tab-btn ${activeTab === "cards_back" ? "active" : ""}`} onClick={() => setActiveTab("cards_back")}>Cards (Retrocesso)</button>
            </div>
          )}
        </div>

        {activeTab === "cards" && (
          <div className="tp-locks-section">
            <p className="tp-lock-hint">
              Marque os campos obrigatórios para que o lead possa <strong>avançar</strong> desta etapa.
            </p>
            {cardGroups.length === 0 ? (
              <p className="text-muted">Nenhum card configurado para este funil.</p>
            ) : (
              cardGroups.map(group => (
                <div key={group.id} className="tp-lock-group">
                  <h6 className="tp-lock-group-title">
                    {group.nome} {group.padrao && <span className="badge bg-secondary">Global</span>}
                  </h6>
                  <div className="row">
                    {group.fields && group.fields.map((field: any) => (
                      <div key={field.id} className="col-md-6 mb-2">
                        <div className="form-check d-flex align-items-center tp-lock-field">
                          <input className="form-check-input me-2" type="checkbox" id={`lock-${field.id}`} checked={lockedFields.includes(field.id)} onChange={() => toggleLock(field.id)} />
                          <label className="form-check-label" htmlFor={`lock-${field.id}`}>
                            {field.label} <small className="text-muted ms-1">({fieldTypes[field.tipo] || field.tipo})</small>
                          </label>
                        </div>
                      </div>
                    ))}
                    {(!group.fields || group.fields.length === 0) && <p className="text-muted ms-2" style={{ fontSize: '0.82rem' }}>Sem campos neste grupo.</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "cards_back" && (
          <div className="tp-locks-section">
            <p className="tp-lock-hint">
              Marque os campos obrigatórios para que o lead possa <strong>retroceder</strong> desta etapa.
            </p>
            {cardGroups.length === 0 ? (
              <p className="text-muted">Nenhum card configurado para este funil.</p>
            ) : (
              cardGroups.map(group => (
                <div key={group.id} className="tp-lock-group">
                  <h6 className="tp-lock-group-title text-danger-title">
                    {group.nome} {group.padrao && <span className="badge bg-secondary">Global</span>}
                  </h6>
                  <div className="row">
                    {group.fields && group.fields.map((field: any) => (
                      <div key={field.id} className="col-md-6 mb-2">
                        <div className="form-check d-flex align-items-center tp-lock-field">
                          <input className="form-check-input me-2" type="checkbox" id={`lock-back-${field.id}`} checked={lockedFieldsBack.includes(field.id)} onChange={() => toggleLockBack(field.id)} />
                          <label className="form-check-label" htmlFor={`lock-back-${field.id}`}>
                            {field.label} <small className="text-muted ms-1">({fieldTypes[field.tipo] || field.tipo})</small>
                          </label>
                        </div>
                      </div>
                    ))}
                    {(!group.fields || group.fields.length === 0) && <p className="text-muted ms-2" style={{ fontSize: '0.82rem' }}>Sem campos neste grupo.</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "cadencias" && (
          <>
            <div className="mb-3">
              <div className="tp-schedule-section">
                <h6 className="tp-schedule-title">
                  <i className="ti ti-calendar-event" />
                  Agendamento de Disparos
                </h6>

                <div className="mb-3">
                  <label className="tp-form-label" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dias da Semana</label>
                  <div className="tp-day-chips">
                    {diasDaSemanaOpcoes.map(dia => (
                      <div
                        key={dia}
                        className={`tp-day-chip ${diasSelecionados.includes(dia) ? 'active' : ''}`}
                        onClick={() => {
                          setDiasSelecionados(prev =>
                            prev.includes(dia) ? prev.filter(d => d !== dia) : [...prev, dia]
                          );
                        }}
                      >
                        {dia.split('-')[0].charAt(0).toUpperCase() + dia.split('-')[0].slice(1)}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <label className="tp-form-label" style={{ fontSize: '0.75rem' }}>Horário de Início</label>
                    <select className="form-select" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)}>
                      <option value="">Selecione...</option>
                      {horasOpcoes.map(hora => <option key={hora.value} value={hora.value}>{hora.label}:00</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="tp-form-label" style={{ fontSize: '0.75rem' }}>Horário de Fim</label>
                    <select className="form-select" value={horaFim} onChange={(e) => setHoraFim(e.target.value)}>
                      <option value="">Selecione...</option>
                      {horasOpcoes.map(hora => <option key={hora.value} value={hora.value}>{hora.label}:00</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="tp-message-section">
                <div className="tp-message-header">
                  <span className="tp-message-label">Mensagem Inicial</span>
                  {!templateBloqueado && (
                    <button type="button" className="tp-expand-btn" onClick={() => openFullScreen("Mensagem Inicial", singleMessage, setSingleMessage)}>
                      <i className="ti ti-arrows-maximize" />
                    </button>
                  )}
                  <div className="position-relative d-inline-block">
                    <button type="button" className="tp-media-btn" onClick={() => setShowDropdown((prev) => !prev)}>
                      <i className="ti ti-paperclip" /> Mídia
                    </button>
                    {showDropdown && (
                      <div className="dropdown-menu show tp-dropdown-menu" style={{ display: "block", position: "absolute", top: "100%", left: 0, minWidth: "160px", zIndex: 10 }}>
                        <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleUploadType("image")}><i className="ti ti-camera" /> Imagem</button>
                        <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleUploadType("video")}><i className="ti ti-movie" /> Vídeo</button>
                        <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleUploadType("document")}><i className="ti ti-paperclip" /> Documento</button>
                      </div>
                    )}
                  </div>
                  {ehOficialResolvido && usaTemplate && (
                    <button type="button" className="tp-media-btn" style={{ borderColor: "#16a34a", color: "#16a34a" }} onClick={() => openTemplatePicker((name) => addBvTemplate(name), true)}>
                      <i className="ti ti-template" /> + Templates
                    </button>
                  )}
                  <button type="button" className="tp-clear-btn" onClick={() => { setSingleMessage(""); setBvTemplates([]); setMidiaBoasVindasUrl(""); setMidiaBoasVindasTipo("video"); }}>Limpar</button>
                  {/* Liga/desliga: boas-vindas em qualquer horário. O botão mostra o INVERSO de
                      funil.respeita_data_on — no banco TRUE significa "respeita a janela". */}
                  <button
                    type="button"
                    className="tp-media-btn"
                    style={{
                      borderColor: !respeitaDataOn ? "#16a34a" : "#cbd5e1",
                      color: !respeitaDataOn ? "#16a34a" : "#64748b",
                      background: !respeitaDataOn ? "rgba(34,197,94,0.08)" : undefined,
                    }}
                    onClick={toggleRespeitaData}
                    title="Quando ligado (SIM), a mensagem de boas-vindas é enviada em qualquer horário. Em NÃO, só dentro dos dias e horas configurados abaixo."
                  >
                    <i className="ti ti-clock-hour-4" /> Qualquer horário: {respeitaDataOn ? "NÃO" : "SIM"}
                  </button>
                </div>
                <div style={{ fontSize: "0.72rem", color: "#64748b", margin: "2px 0 8px", display: "flex", alignItems: "flex-start", gap: 4 }}>
                  <i className="ti ti-info-circle" style={{ marginTop: 1 }} />
                  <span>
                    <b>Qualquer horário</b>: se <b>SIM</b>, a mensagem de boas-vindas é enviada assim que o lead entra na etapa, a qualquer hora e em qualquer dia. Se <b>NÃO</b>, ela só sai dentro dos dias e do horário configurados aqui — lead que entrar fora da janela fica esperando a próxima abertura.
                  </span>
                </div>
                {midiaBoasVindasUrl && (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="tp-media-badge"><i className="ti ti-check" /> Mídia vinculada</span>
                    <button type="button" className="tp-media-btn" style={{ fontSize: '0.72rem', padding: '3px 8px' }} onClick={() => window.open(midiaBoasVindasUrl.split(" | ")[0], "_blank")}>Abrir</button>
                    <button type="button" className="tp-clear-btn" style={{ fontSize: '0.72rem', padding: '3px 8px', color: '#ef4444', borderColor: '#fca5a5' }} onClick={() => setMidiaBoasVindasUrl("")}>Limpar</button>
                  </div>
                )}
                {templateBloqueado ? (
                  /* 🆕 Modo Template: vários templates, cada um com sua % (soma = 100). */
                  <div className="tp-tpl-multi">
                    {bvTemplates.length === 0 ? (
                      <div style={{ fontSize: "0.8rem", color: "#64748b", padding: "10px 12px", background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 8 }}>
                        <i className="ti ti-template me-1" />
                        Clique em <b>+ Templates</b> para vincular um ou mais templates. Cada um recebe uma % de chance de envio (a soma fecha 100 automaticamente).
                      </div>
                    ) : (
                      <>
                        <div className="d-flex flex-column gap-2">
                          {bvTemplates.map((t, i) => (
                            <div key={t.name} className="d-flex align-items-center gap-2" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 10px" }}>
                              <i className="ti ti-template" style={{ color: "#16a34a" }} />
                              <span className="flex-grow-1 text-truncate" style={{ fontSize: "0.82rem", fontWeight: 600, color: "#334155" }} title={t.name}>{t.name}</span>
                              <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                                <input
                                  type="number" min={0} max={100} step={1}
                                  value={t.prob}
                                  onChange={(e) => setBvProb(i, parseInt(e.target.value || "0", 10))}
                                  style={{ width: 62, textAlign: "right", border: "1px solid #cbd5e1", borderRadius: 6, padding: "3px 6px", fontSize: "0.82rem" }}
                                />
                                <span style={{ fontSize: "0.82rem", color: "#64748b" }}>%</span>
                              </div>
                              <button type="button" className="tp-clear-btn" style={{ fontSize: "0.72rem", padding: "3px 8px", color: "#ef4444", borderColor: "#fca5a5", flexShrink: 0 }} onClick={() => removeBvTemplate(i)}>
                                <i className="ti ti-trash" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="d-flex align-items-center justify-content-end gap-2 mt-2">
                          <span style={{ fontSize: "0.78rem", fontWeight: 700, color: somaBv === 100 ? "#16a34a" : "#ef4444" }}>
                            <i className={`ti ${somaBv === 100 ? "ti-circle-check" : "ti-alert-circle"} me-1`} />
                            Soma: {somaBv}%
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <textarea
                    className="form-control"
                    value={singleMessage}
                    rows={3}
                    onChange={(e) => setSingleMessage(e.target.value)}
                    placeholder="Digite a mensagem inicial..."
                  />
                )}
              </div>
            </div>

            {templateBloqueado && (
              /* 🆕 Agora cada cadência tem sua PRÓPRIA lista de templates + %.
                 A configuração fica dentro de cada "Cadência N" abaixo. */
              <div style={{ fontSize: "0.72rem", color: "#64748b", margin: "0 0 6px", display: "flex", alignItems: "flex-start", gap: 4 }}>
                <i className="ti ti-info-circle" style={{ marginTop: 1 }} />
                <span>No modo Template, <b>cada cadência</b> tem sua própria lista de templates com % independente. Configure dentro de cada cadência abaixo (a soma deve fechar 100%).</span>
              </div>
            )}

            {cadenciaList.map((item, index) => (
              <div key={index} className="tp-cadence-item">
                <div className="tp-cadence-header">
                  <span className="tp-cadence-number">Cadência {index + 1}</span>
                  {!templateBloqueado && (
                    <button type="button" className="tp-expand-btn" onClick={() => openFullScreen(`Cadência ${index + 1}`, item.mensagem, (val) => { const newList = [...cadenciaList]; newList[index].mensagem = val; setCadenciaList(newList); })}>
                      <i className="ti ti-arrows-maximize" />
                    </button>
                  )}

                  <div className="tp-time-input">
                    <input type="number" placeholder="Tempo" value={item.cadencia} onChange={(e) => { const v = e.target.value.slice(0, 3); const newList = [...cadenciaList]; newList[index].cadencia = v; setCadenciaList(newList); }} />
                    <div className="tp-unidade-wrapper" style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="tp-unidade-btn"
                        onClick={() => {
                          const newList = [...cadenciaList];
                          (newList[index] as any).showUnidadeDropdown = !(item as any).showUnidadeDropdown;
                          // Fecha o dropdown de mídia se estiver aberto
                          (newList[index] as any).showDropdown = false;
                          setCadenciaList(newList);
                        }}
                      >
                        {item.unidade === 'minutos' ? 'min' : item.unidade === 'horas' ? 'hrs' : 'dias'}
                        <i className="ti ti-chevron-down ms-1" style={{ fontSize: '0.7rem' }} />
                      </button>
                      {(item as any).showUnidadeDropdown && (
                        <div
                          className="dropdown-menu show tp-dropdown-menu"
                          style={{ display: "block", position: "absolute", top: "100%", right: 0, minWidth: "90px", zIndex: 1050 }}
                        >
                          {[
                            { value: 'minutos', label: 'min' },
                            { value: 'horas', label: 'hrs' },
                            { value: 'dias', label: 'dias' },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={`dropdown-item ${item.unidade === opt.value ? 'active' : ''}`}
                              onClick={() => {
                                const newList = [...cadenciaList];
                                newList[index].unidade = opt.value as Cadencia["unidade"];
                                (newList[index] as any).showUnidadeDropdown = false;
                                setCadenciaList(newList);
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="position-relative d-inline-block">
                    <button type="button" className="tp-media-btn" onClick={() => { const newList = [...cadenciaList]; (newList[index] as any).showDropdown = !(item as any).showDropdown; setCadenciaList(newList); }}>
                      <i className="ti ti-paperclip" /> Mídia
                    </button>
                    {(item as any).showDropdown && (
                      <div className="dropdown-menu show tp-dropdown-menu" style={{ display: "block", position: "absolute", top: "100%", left: 0, minWidth: "160px", zIndex: 1050 }}>
                        {["image", "video", "document"].map((tipo) => (
                          <button key={tipo} className="dropdown-item d-flex align-items-center gap-2" onClick={async () => {
                            const input = document.createElement("input"); input.type = "file";
                            input.accept = tipo === "image" ? "image/*" : tipo === "video" ? "video/*" : ".pdf,.doc,.docx,.xlsx";
                            input.onchange = async (e: any) => { const file = e.target.files?.[0]; if (!file) return; const formData = new FormData(); formData.append("file", file); formData.append("mediatype", tipo);
                              const res = await fetch(`${API_URL}/upload_cadencia_midia`, { method: "POST", body: formData }); const data = await res.json();
                              if (data.url) { const newList = [...cadenciaList]; (newList[index] as any).midiaUrl = data.url; (newList[index] as any).midiaTipo = tipo; (newList[index] as any).showDropdown = false; setCadenciaList(newList); } else { message.error("Erro ao enviar mídia"); }
                            }; input.click();
                          }}>
                            {tipo === "image" && <i className="ti ti-camera" />}
                            {tipo === "video" && <i className="ti ti-movie" />}
                            {tipo === "document" && <i className="ti ti-paperclip" />}
                            {tipo === "image" ? "Imagem" : tipo === "video" ? "Vídeo" : "Documento"}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {(item as any).midiaUrl && (
                    <div className="d-flex align-items-center gap-2">
                      <span className="tp-media-badge"><i className="ti ti-check" /> Mídia</span>
                      <button type="button" className="tp-media-btn" style={{ fontSize: '0.72rem', padding: '3px 8px' }} onClick={() => { const url = (item as any).midiaUrl?.split(" | ")[0]; if (url) window.open(url, "_blank"); }}>Abrir</button>
                      <button type="button" className="tp-clear-btn" style={{ fontSize: '0.72rem', padding: '3px 8px', color: '#ef4444', borderColor: '#fca5a5' }} onClick={() => { const newList = [...cadenciaList]; (newList[index] as any).midiaUrl = ""; setCadenciaList(newList); }}>Limpar</button>
                    </div>
                  )}

                  <button type="button" className="tp-clear-btn" onClick={() => { const newList = [...cadenciaList]; const novo: any = { cadencia: "", unidade: "dias", mensagem: "", tpls: [], probs: [] }; newList[index] = novo; setCadenciaList(newList); }}>Limpar</button>
                </div>
                {templateBloqueado ? (
                  /* 🆕 Modo Template: cada cadência tem sua PRÓPRIA lista de templates + %.
                     (mesma lógica das boas-vindas, mas por cadência) */
                  (() => {
                    const tpls = (((item as any).tpls as string[]) || []);
                    const probs = (((item as any).probs as number[]) || []);
                    const soma = somaCadFor(probs);
                    return (
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                          <button type="button" className="tp-media-btn" style={{ borderColor: "#16a34a", color: "#16a34a" }}
                            onClick={() => openTemplatePicker((name) => addCadTpl(index, name), true)}>
                            <i className="ti ti-plus" /> Template
                          </button>
                          {tpls.length > 0 && (
                            <span style={{ fontSize: "0.78rem", fontWeight: 700, color: soma === 100 ? "#16a34a" : "#ef4444", marginLeft: "auto" }}>
                              <i className={`ti ${soma === 100 ? "ti-circle-check" : "ti-alert-circle"} me-1`} />
                              Soma: {soma}%
                            </span>
                          )}
                        </div>
                        {tpls.length === 0 ? (
                          <div style={{ fontSize: "0.8rem", color: "#94a3b8", fontStyle: "italic", padding: "4px 2px" }}>
                            Nenhum template. Clique em <b>+ Template</b> para adicionar (com % de envio).
                          </div>
                        ) : (
                          tpls.map((nome, pos) => (
                            <div key={pos} className="d-flex align-items-center gap-2" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 10px" }}>
                              <span className="flex-grow-1 text-truncate" style={{ fontSize: "0.82rem", fontWeight: 600, color: "#334155" }} title={nome}>
                                <i className="ti ti-template me-1" style={{ color: "#16a34a" }} />{nome || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>—</span>}
                              </span>
                              <input
                                type="number" min={0} max={100} step={1}
                                value={probs[pos] ?? 0}
                                onChange={(e) => setCadTplProb(index, pos, parseInt(e.target.value || "0", 10))}
                                style={{ width: 58, textAlign: "right", border: "1px solid #cbd5e1", borderRadius: 6, padding: "3px 6px", fontSize: "0.82rem", flexShrink: 0 }}
                              />
                              <span style={{ fontSize: "0.82rem", color: "#64748b", flexShrink: 0 }}>%</span>
                              <button type="button" className="tp-media-btn" style={{ borderColor: "#16a34a", color: "#16a34a", flexShrink: 0 }} onClick={() => openTemplatePicker((name) => changeCadTpl(index, pos, name))}>
                                <i className="ti ti-template" /> Trocar
                              </button>
                              <button type="button" className="tp-clear-btn" style={{ fontSize: "0.72rem", padding: "3px 8px", color: "#ef4444", borderColor: "#fca5a5", flexShrink: 0 }} onClick={() => removeCadTpl(index, pos)}>
                                <i className="ti ti-x" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <textarea
                    className="form-control"
                    placeholder="Mensagem da cadência..."
                    value={item.mensagem}
                    rows={3}
                    onChange={(e) => { const newList = [...cadenciaList]; newList[index].mensagem = e.target.value; setCadenciaList(newList); }}
                  />
                )}
              </div>
            ))}

            <div className="tp-message-section">
              <div className="tp-message-header">
                <span className="tp-message-label">Mensagem de Retorno Agendado</span>
                {!templateBloqueado && (
                  <button type="button" className="tp-expand-btn" onClick={() => openFullScreen("Mensagem de Retorno", returnMessage, setReturnMessage)}>
                    <i className="ti ti-arrows-maximize" />
                  </button>
                )}
                <div className="position-relative d-inline-block">
                  <button type="button" className="tp-media-btn" onClick={() => setShowDropdown((prev) => !prev)}>
                    <i className="ti ti-paperclip" /> Mídia
                  </button>
                  {showDropdown && (
                    <div className="dropdown-menu show tp-dropdown-menu" style={{ display: "block", position: "absolute", top: "100%", left: 0, minWidth: "160px", zIndex: 10 }}>
                      <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleUploadType("image")}><i className="ti ti-camera" /> Imagem</button>
                      <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleUploadType("video")}><i className="ti ti-movie" /> Vídeo</button>
                      <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleUploadType("document")}><i className="ti ti-paperclip" /> Documento</button>
                    </div>
                  )}
                </div>
                {ehOficialResolvido && usaTemplate && (
                  <button type="button" className="tp-media-btn" style={{ borderColor: "#16a34a", color: "#16a34a" }} onClick={() => openTemplatePicker((name) => setReturnMessage(name))}>
                    <i className="ti ti-template" /> Template
                  </button>
                )}
                <button type="button" className="tp-clear-btn" onClick={() => { setReturnMessage(""); setMidiaRetornoUrl(""); setMidiaRetornoTipo("video"); }}>Limpar</button>
              </div>
              {midiaRetornoUrl && (
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="tp-media-badge"><i className="ti ti-check" /> Mídia vinculada</span>
                  <button type="button" className="tp-media-btn" style={{ fontSize: '0.72rem', padding: '3px 8px' }} onClick={() => window.open(midiaRetornoUrl.split(" | ")[0], "_blank")}>Abrir</button>
                  <button type="button" className="tp-clear-btn" style={{ fontSize: '0.72rem', padding: '3px 8px', color: '#ef4444', borderColor: '#fca5a5' }} onClick={() => setMidiaRetornoUrl("")}>Limpar</button>
                </div>
              )}
              <textarea
                className="form-control"
                rows={2}
                value={returnMessage}
                onChange={(e) => setReturnMessage(e.target.value)}
                readOnly={templateBloqueado}
                placeholder={templateBloqueado ? "Use o botão Template para escolher um template…" : "Mensagem de retorno agendado..."}
                style={templateBloqueado ? { background: "#f1f5f9", cursor: "not-allowed", color: "#475569", fontWeight: 600 } : undefined}
              />
            </div>
          </>
        )}
        
        {activeTab === "prompts" && (
          <div className="tp-prompts-section">
            <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <label className="tp-form-label mb-0">Quantidade de Caracteres:</label>
                <select className="form-select" style={{ maxWidth: "180px" }} value={qtdeCaracteres} onChange={(e) => setQtdeCaracteres(e.target.value)}>
                  <option value="Pequena">Pequena</option>
                  <option value="Média">Média</option>
                  <option value="Alta">Alta</option>
                </select>
              </div>

              {/* Follow de Engajamento — grava em funil_pai.follow / funil_pai.follow_intervalos.
                  Escopo é o FUNIL INTEIRO, não a etapa aberta: por isso o aviso ao lado. */}
              <div className="d-flex align-items-center gap-2">
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="tp-follow-switch"
                    checked={followHabilitado}
                    onChange={(e) => setFollowHabilitado(e.target.checked)}
                  />
                </div>
                <label className="tp-form-label mb-0" htmlFor="tp-follow-switch">Follow de Engajamento:</label>
                <input
                  type="text"
                  className="form-control"
                  style={{ maxWidth: "180px" }}
                  value={followIntervalos}
                  onChange={(e) => setFollowIntervalos(e.target.value)}
                  placeholder="Ex.: 3,10,30"
                  title="Intervalos em minutos, separados por vírgula. A quantidade de números é o máximo de follows na janela de silêncio."
                />
              </div>
              <div style={{ fontSize: "0.72rem", color: "#64748b", width: "100%", marginTop: -4 }}>
                {followPorEtapa ? (
                  <>Vale só para a etapa <strong>"{currentStage}"</strong>. Cada etapa tem a sua régua e as suas instruções.</>
                ) : (
                  <>
                    O Follow de Engajamento vale para <strong>todo o funil "{currentFunnel}"</strong>, não só para esta etapa.
                    {/* `motivo` vem do backend e distingue os casos de fonte=pai: não
                        migrado, marcado sem config em etapa nenhuma, ou freio ligado. */}
                    {followFonteMotivo && (
                      <span style={{ display: "block", marginTop: 2 }}>{followFonteMotivo}</span>
                    )}
                  </>
                )}
                {/* Botão de migrar: só aparece com `pode_migrar` do backend, que exige pelo
                    menos uma etapa já configurada. É o passo 2 do runbook — o passo 1 é
                    copiar a config do pai para as etapas (scripts/migrar_follow_por_etapa.py).
                    Sem essa ordem a guarda do webhook-meta recusa a flag e a tela mostraria
                    "migrado" com o disparo ainda saindo pelo pai. */}
                {!followPorEtapa && followIdPai && !followFreio && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mt-1"
                    disabled={migrandoFunil}
                    onClick={async () => {
                      if (!window.confirm(
                        `Migrar o funil "${currentFunnel}" para follow por etapa?` +
                        "\n\n" +
                        "A partir daí cada etapa passa a ter a sua própria configuração, e os " +
                        "campos do funil deixam de ter efeito. Etapa sem configuração não faz " +
                        "follow — não há herança do funil." +
                        "\n\nVale em até 60 segundos."
                      )) return;
                      setMigrandoFunil(true);
                      try {
                        const res = await fetch(`${API_URL}/migrar_follow_por_etapa`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ id_pai: followIdPai, migrar: true }),
                        });
                        const d = await res.json();
                        if (!res.ok) { alert(d?.error || "Não foi possível migrar o funil."); return; }
                        // Reconsulta em vez de assumir: quem diz o escopo é o backend.
                        const rf = await fetch(`${API_URL}/follow_fonte?id_pai=${followIdPai}`);
                        const df = await rf.json();
                        const f = (df?.fontes || [])[0];
                        if (f) {
                          setFollowPorEtapa(f.fonte === "etapa");
                          setFollowFonteMotivo(f.motivo || "");
                          setFollowPodeMigrar(f.pode_migrar === true);
                        }
                        alert("Funil migrado. Cada etapa passa a ter a sua própria configuração de follow." +
                              "\n\nVale em até 60 segundos.");
                      } catch {
                        alert("Não foi possível migrar o funil.");
                      } finally {
                        setMigrandoFunil(false);
                      }
                    }}
                  >
                    {migrandoFunil ? "Migrando…" : "Migrar este funil para follow por etapa"}
                  </button>
                )}
                {/* Resumo do funil. Sem herança, etapa sem config é etapa muda — então
                    "o follow deste funil está funcionando?" só se responde olhando todas as
                    etapas, não a que está aberta. Montado de `allStages`, que já traz
                    follow_etapa/closer de todas: não precisa de chamada nova. */}
                {followPorEtapa && (() => {
                  const doFunil = allStages.filter((s: any) => s.funil === currentFunnel);
                  // "Atender" = closer 'Sim' na ETAPA **e** no FUNIL PAI — o mesmo critério do
                  // motor de follow. Contar só pelo closer da etapa faz a tela divergir do
                  // backend justamente nos funis de pai desligado (5 hoje), onde ele devolve
                  // as contagens zeradas de propósito ainda que a etapa diga 'Sim'.
                  const paiAtende = doFunil.some((s: any) => s.closer_pai === "Sim");
                  const atendem = paiAtende
                    ? doFunil.filter((s: any) => s.closer === "Sim")
                    : [];
                  const comConfig = atendem.filter((s: any) => s.follow_etapa != null);
                  const ligadas = atendem.filter((s: any) => s.follow_etapa === "Sim");
                  const mudas = atendem.filter((s: any) => s.follow_etapa == null);
                  return (
                    <div style={{ fontSize: "0.72rem", color: "#64748b", width: "100%" }}>
                      Neste funil: {atendem.length} etapa{atendem.length === 1 ? "" : "s"} que
                      atende{atendem.length === 1 ? "" : "m"}, {comConfig.length} configurada
                      {comConfig.length === 1 ? "" : "s"}, <strong>{ligadas.length} com follow ligado</strong>.
                      {mudas.length > 0 && (
                        <span style={{ display: "block" }}>
                          Sem configuração (não fazem follow): {mudas.map((s: any) => s.nome_funil).join(", ")}.
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Reverter: as colunas das etapas NÃO são apagadas, então migrar de novo
                    depois recupera a configuração — o operador não perde trabalho. */}
                {followPorEtapa && followIdPai && !followFreio && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-1"
                    disabled={migrandoFunil}
                    onClick={async () => {
                      if (!window.confirm(
                        `Reverter o funil "${currentFunnel}" para a configuração do funil?` +
                        "\n\n" +
                        "O follow volta a ser o do funil inteiro, e os campos do funil voltam " +
                        "a valer para todas as etapas." +
                        "\n\nA configuração de cada etapa NÃO é apagada: se migrar de novo " +
                        "depois, ela volta como está agora."
                      )) return;
                      setMigrandoFunil(true);
                      try {
                        const res = await fetch(`${API_URL}/migrar_follow_por_etapa`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ id_pai: followIdPai, migrar: false }),
                        });
                        const d = await res.json();
                        if (!res.ok) { alert(d?.error || "Não foi possível reverter."); return; }
                        const rf = await fetch(`${API_URL}/follow_fonte?id_pai=${followIdPai}`);
                        const f = ((await rf.json())?.fontes || [])[0];
                        if (f) {
                          setFollowPorEtapa(f.fonte === "etapa");
                          setFollowFonteMotivo(f.motivo || "");
                          setFollowPodeMigrar(f.pode_migrar === true);
                        }
                        alert("Funil revertido: o follow volta a ser o do funil inteiro." +
                              "\n\nVale em até 60 segundos.");
                      } catch {
                        alert("Não foi possível reverter.");
                      } finally {
                        setMigrandoFunil(false);
                      }
                    }}
                  >
                    {migrandoFunil ? "Revertendo…" : "Reverter para configuração do funil"}
                  </button>
                )}

                {followFreio && (
                  <span style={{ display: "block", marginTop: 2, color: "#b45309" }}>
                    O backend está com o freio de emergência ligado: todos os funis estão lendo a
                    configuração do funil pai.
                  </span>
                )}
              </div>

              {/* Validação da régua — espelha o parser do backend. O caso que mais dói é o
                  token ruim no meio de tokens bons: o backend descarta só ele e o teto de
                  follows encolhe junto, sem nada avisar. */}
              {followHabilitado && followIntervalos.trim() && (
                <div style={{ fontSize: "0.72rem", width: "100%", marginTop: -2 }}>
                  {reguaInfo.erros.length > 0 ? (
                    <span style={{ color: "#b91c1c" }}>
                      {reguaInfo.erros.join(" · ")}
                      {reguaInfo.tokens > 0 && <> — valeriam só {reguaInfo.tokens} tentativa{reguaInfo.tokens > 1 ? "s" : ""}.</>}
                    </span>
                  ) : (
                    <span style={{ color: "#64748b" }}>
                      Até {reguaInfo.tokens} tentativa{reguaInfo.tokens > 1 ? "s" : ""} de follow.
                    </span>
                  )}
                </div>
              )}

              {/* Etapa sem closer nunca faz follow: o gate de atendimento exige closer='Sim'
                  na etapa E no pai, então configurar aqui seria config que nunca dispara. */}
              {followPorEtapa && followHabilitado && !atendeDeFato && (
                <div style={{ fontSize: "0.72rem", color: "#b45309", width: "100%" }}>
                  {closerHabilitado
                    ? <>O <strong>funil</strong> está com o closer desligado: o follow nunca vai disparar nesta etapa.</>
                    : <>Esta etapa não atende (closer desligado): o follow nunca vai disparar aqui.</>}
                </div>
              )}

              {/* follow_prompt — instruções gerais do follow. Entra como um bloco no prompt,
                  não substitui as regras do sistema. Nunca chegou a ser exposto para o pai. */}
              <div style={{ width: "100%" }}>
                <label className="tp-form-label mb-1">Instruções gerais do follow:</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={followPrompt}
                  onChange={(e) => setFollowPrompt(e.target.value)}
                  placeholder="Ex.: o assunto é sempre o caso do lead, nunca a conversa em si. Uma pergunta por mensagem."
                  style={{ fontSize: "0.82rem" }}
                />
              </div>

              {/* Papel de cada tentativa — funil_pai.follow_instrucao_1..3. Entram no prompt do
                  follow JUNTO das regras do arquivo padrão, não no lugar delas. Vazio = só o padrão. */}
              <div style={{ width: "100%" }}>
                <label className="tp-form-label mb-1">Instruções por tentativa de follow:</label>
                {([
                  ["1ª tentativa", followInstrucao1, setFollowInstrucao1, "Ex.: Cite o nome do lead e retome o dado que ficou faltando."],
                  ["2ª tentativa", followInstrucao2, setFollowInstrucao2, "Ex.: Reengaje por outro ângulo, oferecendo resolver com menos esforço."],
                  ["3ª tentativa em diante", followInstrucao3, setFollowInstrucao3, "Ex.: Fecho leve, sem pressão, deixando a porta aberta."],
                ] as const).map(([rotulo, valor, setter, exemplo]) => (
                  <div key={rotulo} className="mb-2">
                    <label style={{ fontSize: "0.72rem", color: "#64748b" }}>{rotulo}</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={valor}
                      onChange={(e) => setter(e.target.value)}
                      placeholder={exemplo}
                      style={{ fontSize: "0.82rem" }}
                    />
                  </div>
                ))}
                <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
                  Deixar em branco usa só o prompt padrão. Se houver mais de 3 intervalos, as tentativas
                  seguintes herdam a última instrução preenchida.
                </div>
              </div>
            </div>

            {/* Campo único: o PROMPT FINAL da etapa (funil.prompt_melhorado), editado direto.
                Antes havia 3 campos (Ação Esperada / Objetivo / Instruções) que eram a ENTRADA
                do gerador — salvar zerava o prompt_melhorado e marcava fluxo_melhorar='Sim'
                para a IA reescrever. Agora o texto final é editado aqui e salvo como está;
                os 3 campos antigos continuam no banco, intactos. */}
            <div className="tp-prompt-field">
              <div className="tp-prompt-label">
                <label>Prompt</label>
                <button className="tp-expand-btn" onClick={() => openFullScreen("Prompt", promptMelhorado, setPromptMelhorado)}><i className="ti ti-arrows-maximize" /></button>
              </div>
              <textarea
                className="form-control"
                rows={18}
                value={promptMelhorado}
                onChange={(e) => setPromptMelhorado(e.target.value)}
                placeholder="Prompt do closer para esta etapa..."
                style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: "0.82rem", lineHeight: 1.5 }}
              />
              <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: 4 }}>
                {promptMelhorado.length.toLocaleString("pt-BR")} caracteres
              </div>
            </div>
            {/* 🔹 Mídia de Vídeo do Funil */}
            <div className="mb-4">
              <label className="form-label fw-semibold">Vídeo do Funil</label>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center"
                  onClick={async () => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "video/*";
                    input.onchange = async (e: any) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append("file", file);
                      formData.append("mediatype", "video");
                      const res = await fetch(`${API_URL}/upload_midia_inst`, {
                        method: "POST",
                        body: formData,
                      });
                      const data = await res.json();
                      if (data.url) setUrlVideoFunil(data.url);
                      else message.error("Erro ao enviar vídeo");
                    };
                    input.click();
                  }}
                >
                  <i className="ti ti-movie me-1" /> Enviar Vídeo
                </button>
                {urlVideoFunil && (
                  <>
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => window.open(urlVideoFunil, "_blank")}
                    >
                      Abrir
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setUrlVideoFunil("")}
                    >
                      Remover
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* 🔹 Mídia de Imagem do Funil */}
            <div className="mb-4">
              <label className="form-label fw-semibold">Imagem do Funil</label>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center"
                  onClick={async () => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = async (e: any) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append("file", file);
                      formData.append("mediatype", "image");
                      const res = await fetch(`${API_URL}/upload_midia_inst`, {
                        method: "POST",
                        body: formData,
                      });
                      const data = await res.json();
                      if (data.url) setUrlImagemFunil(data.url);
                      else message.error("Erro ao enviar imagem");
                    };
                    input.click();
                  }}
                >
                  <i className="ti ti-camera me-1" /> Enviar Imagem
                </button>
                {urlImagemFunil && (
                  <>
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => window.open(urlImagemFunil, "_blank")}
                    >
                      Abrir
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setUrlImagemFunil("")}
                    >
                      Remover
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {activeTab === "redirecionamento" && verificarCloser && (
          <>
            <p className="tp-lock-hint" style={{ marginBottom: '16px' }}>
              Configure até 3 redirecionamentos. Selecione a etapa de destino e descreva a instrução.
            </p>

            {[0, 1, 2].map((i) => (
              <div key={i} className="tp-redirect-item">
                <span className="tp-redirect-number">Redirecionamento {i + 1}</span>
                <div className="tp-prompt-field">
                  <div className="tp-prompt-label">
                    <label>Instrução</label>
                    <button className="tp-expand-btn" onClick={() => openFullScreen(`Instrução ${i+1}`, redirecionamentos[i]?.instrucao || "", (val) => handleInstrucaoChange(i, val))}><i className="ti ti-arrows-maximize" /></button>
                  </div>
                  <textarea className="form-control" rows={2} value={redirecionamentos[i]?.instrucao || ""} onChange={(e) => handleInstrucaoChange(i, e.target.value)} placeholder="Quando usar este redirecionamento..." />
                </div>
                <div className="tp-form-group" style={{ marginBottom: 0 }}>
                  <label className="tp-form-label">Etapa de Destino</label>
                  <select className="form-select" value={redirecionamentos[i]?.etapa_destino_id || ""} onChange={(e) => handleEtapaChange(i, e.target.value)}>
                    <option value="">Selecione uma etapa</option>
                    {allStages.filter((s) => s.funil === currentFunnel && s.nome_funil !== currentStage).sort((a, b) => a.ordem - b.ordem).map((etapa) => (
                      <option key={etapa.id} value={etapa.id}>{etapa.nome_funil}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="tp-btn-cancel" onClick={onHide}>Cancelar</button>
        <button className="tp-btn-save" onClick={handleAplicar} disabled={isSaving}>
          {isSaving ? "Salvando..." : "Aplicar"}
        </button>
      </Modal.Footer>
    </Modal>

    {fullScreenField && (
      <Modal show={true} onHide={() => setFullScreenField(null)} centered fullscreen dialogClassName="tp-fullscreen-modal">
        <Modal.Header closeButton>
          <Modal.Title>{fullScreenField.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <textarea
            className="form-control"
            style={{ height: '100%', fontSize: '1.1rem', resize: 'none' }}
            value={fullScreenField.value}
            onChange={(e) => setFullScreenField({ ...fullScreenField, value: e.target.value })}
          />
        </Modal.Body>
        <Modal.Footer>
          <button className="tp-btn-cancel" onClick={() => setFullScreenField(null)}>Cancelar</button>
          <button className="tp-btn-save" onClick={() => { fullScreenField.onSave(fullScreenField.value); setFullScreenField(null); }}>Salvar</button>
        </Modal.Footer>
      </Modal>
    )}

    {/* 🆕 Picker de templates — visual portado do /chat (lista + busca) */}
    <Modal show={showTemplatePicker} onHide={() => setShowTemplatePicker(false)} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="ti ti-template text-success" style={{ fontSize: 22 }} />
          <span className="fw-bold">Selecionar template</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {loadingTemplates ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success" role="status" />
            <p className="text-muted mt-3 mb-0 small">Carregando templates da Meta…</p>
          </div>
        ) : (
          <>
            <div className="input-group mb-3">
              <span className="input-group-text bg-white border-end-0">
                <i className="ti ti-search text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Buscar template pelo nome…"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                autoFocus
              />
            </div>
            {(() => {
              const filtered = templatesList.filter((t) =>
                t.name.toLowerCase().includes(templateSearch.toLowerCase())
              );
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-5">
                    <i className="ti ti-mood-empty text-muted" style={{ fontSize: 36 }} />
                    <p className="text-muted mt-2 mb-0 small">Nenhum template aprovado encontrado.</p>
                  </div>
                );
              }
              return (
                <>
                  <div className="text-muted small mb-2 ps-1">
                    {filtered.length} template{filtered.length === 1 ? "" : "s"} aprovado{filtered.length === 1 ? "" : "s"}
                  </div>
                  <div className="d-flex flex-column gap-2" style={{ maxHeight: 460, overflowY: "auto", paddingRight: 4 }}>
                    {filtered.map((t) => {
                      const hasImage = t.headerFormat === "IMAGE" && !!t.headerImage;
                      const hasVideo = t.headerFormat === "VIDEO" && !!t.headerImage;
                      const previewText = (t.body || t.headerText || "(sem corpo)").slice(0, 120);
                      const truncated = (t.body || t.headerText || "").length > 120;
                      return (
                        <button
                          type="button"
                          key={t.name}
                          onClick={() => chooseTemplate(t)}
                          className="w-100 d-flex align-items-start gap-3 p-3 border rounded-3"
                          style={{
                            background: "#ffffff", color: "#1a1a2e", textAlign: "left", cursor: "pointer",
                            transition: "transform .12s, box-shadow .12s, border-color .12s",
                            borderColor: "rgba(0,0,0,0.08)",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#22c55e"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(34,197,94,0.12)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
                        >
                          <div
                            className="flex-shrink-0 d-flex align-items-center justify-content-center"
                            style={{ width: 48, height: 48, borderRadius: 10, background: hasImage ? "transparent" : "rgba(34,197,94,0.08)", overflow: "hidden" }}
                          >
                            {hasImage ? (
                              <img src={t.headerImage} alt={t.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : hasVideo ? (
                              <i className="ti ti-video" style={{ fontSize: 22, color: "#16a34a" }} />
                            ) : (
                              <i className="ti ti-message-circle" style={{ fontSize: 20, color: "#16a34a" }} />
                            )}
                          </div>
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                              <span className="fw-bold" style={{ fontSize: 14 }}>{t.name}</span>
                              {hasImage && (
                                <span className="badge rounded-pill" style={{ background: "rgba(34,197,94,0.12)", color: "#16a34a", fontSize: 10, fontWeight: 700, padding: "3px 8px" }}>
                                  <i className="ti ti-photo me-1" />IMAGEM
                                </span>
                              )}
                              {hasVideo && (
                                <span className="badge rounded-pill" style={{ background: "rgba(59,130,246,0.12)", color: "#2563eb", fontSize: 10, fontWeight: 700, padding: "3px 8px" }}>
                                  <i className="ti ti-video me-1" />VÍDEO
                                </span>
                              )}
                              {t.vars.length > 0 && (
                                <span className="badge rounded-pill" style={{ background: "rgba(99,102,241,0.12)", color: "#6366f1", fontSize: 10, fontWeight: 700, padding: "3px 8px" }}>
                                  <i className="ti ti-variable me-1" />{t.vars.length} var.
                                </span>
                              )}
                            </div>
                            <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {previewText}{truncated && "…"}
                            </div>
                          </div>
                          <i className="ti ti-chevron-right text-muted flex-shrink-0 align-self-center" style={{ fontSize: 18 }} />
                        </button>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        {pickerKeepOpen && (
          <span className="text-muted small me-auto">
            <i className="ti ti-info-circle me-1" />Clique nos templates para adicionar vários; feche quando terminar.
          </span>
        )}
        <button className="tp-btn-cancel" onClick={() => { setShowTemplatePicker(false); setTemplateTarget(null); }}>
          {pickerKeepOpen ? "Concluir" : "Cancelar"}
        </button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default CadenciaModal;
